import { useTranslation } from 'react-i18next';
import { Table, Progress, Flex, type TableProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TTransportPerformanceSeriesRes } from 'api/swagger';
import { useTransformToTableData } from 'shared/hooks/use-transform-to-table-data';
import { numberFormatter } from 'shared/utils/numbers/formatter.ts';
import { round10 } from 'shared/utils/numbers/round.ts';
import { secondsToHoursAndMinutes } from 'shared/utils/time/seconds-to-hours-and-minutes';
import { ALLOWED_HOURS_IN_MINUTES } from './constants';
import styles from './transport-mileage-list.module.scss';
import type { ITransportMileageListProps } from './types';

export const TransportMileageList: React.FC<ITransportMileageListProps> = (props) => {
  const { transportMileages, isLoading, tachoDataMap, selectedIds, onSelectionChange } = props;
  const { t } = useTranslation();
  const tableData = useTransformToTableData(transportMileages);

  const rowSelection: TableProps<TTransportPerformanceSeriesRes>['rowSelection'] = {
    selectedRowKeys: selectedIds,
    onChange: (selectedRowKeys) => onSelectionChange(selectedRowKeys as number[]),
  };

  const columns: ColumnsType<TTransportPerformanceSeriesRes> = [
    {
      title: t('transport:plate_number'),
      dataIndex: 'plateNumber',
      render: (plateNumber: string) => plateNumber,
    },
    {
      title: t('transport-mileage:driving'),
      dataIndex: 'driving',
      render: (_: unknown, { id }: TTransportPerformanceSeriesRes) => {
        const tachoInfo = tachoDataMap.get(id);

        if (!tachoInfo) {
          return '-';
        }

        return tachoInfo.map((item) => {
          const weekDriving = secondsToHoursAndMinutes(item.thisWeekDriving);
          const weekAllowed = secondsToHoursAndMinutes(item.thisWeekDrivingAllowed);
          const twoWeeksDriving = secondsToHoursAndMinutes(item.twoWeeksDriving);
          const twoWeeksAllowed = secondsToHoursAndMinutes(ALLOWED_HOURS_IN_MINUTES);

          return (
            <>
              <div>
                Week: {weekDriving} / {weekAllowed}
              </div>
              <div className={styles.secondary}>
                2Weeks: {twoWeeksDriving} / {twoWeeksAllowed}
              </div>
            </>
          );
        });
      },
    },
    {
      title: t('transport-mileage:ldm_points'),
      dataIndex: 'ldmAndPoints',
      render: (_: any, { totalLdm, pointsCount }) => {
        return `${numberFormatter(totalLdm)} / ${pointsCount}`;
      },
    },
    {
      title: 'Useful, %',
      width: '14%',
      render: (record: TTransportPerformanceSeriesRes) => {
        const { totalMileage, usefulMileage } = record;
        const usefulPercentage = totalMileage && usefulMileage ? round10((usefulMileage / totalMileage) * 100) : 0;
        return (
          <Flex className={styles.progress}>
            <Progress percent={usefulPercentage} size='small' format={() => `${usefulPercentage}%`} />
          </Flex>
        );
      },
    },
    {
      title: t('transport-mileage:total_mileage'),
      dataIndex: 'totalMileage',
      render: (totalMileage: number) => numberFormatter(totalMileage),
    },
    {
      title: t('transport-mileage:total_mileage_cost'),
      dataIndex: 'totalMileageCost',
      render: (totalMileageCost: number) => numberFormatter(totalMileageCost),
    },
    {
      title: t('transport-mileage:useful_mileage'),
      dataIndex: 'usefulMileage',
      render: (usefulMileage: number) => numberFormatter(usefulMileage),
    },
    {
      title: t('transport-mileage:useful_mileage_cost'),
      dataIndex: 'usefulMileageCost',
      render: (usefulMileageCost: number) => numberFormatter(usefulMileageCost),
    },
    {
      title: t('transport-mileage:useless_mileage'),
      dataIndex: 'uselessMileage',
      render: (uselessMileage: number) => numberFormatter(uselessMileage),
    },
    {
      title: t('transport-mileage:useless_mileage_cost'),
      dataIndex: 'uselessMileageCost',
      render: (uselessMileageCost: number) => numberFormatter(uselessMileageCost),
    },
  ];

  return (
    <Table
      size={'small'}
      rowKey={(record) => record.id}
      rowSelection={rowSelection}
      columns={columns}
      dataSource={tableData}
      pagination={false}
      loading={isLoading ? { size: 'large', delay: 200 } : false}
      bordered
    />
  );
};
